// SPDX-FileCopyrightText: 2024 Chris Duncan <chris@zoso.dev>
// SPDX-License-Identifier: GPL-3.0-or-later
import { default as TransportBLE } from '@ledgerhq/hw-transport-web-ble';
import { default as TransportUSB } from '@ledgerhq/hw-transport-webusb';
import { default as TransportHID } from '@ledgerhq/hw-transport-webhid';
import { BIP44_COIN_NANO, BIP44_PURPOSE, HARDENED_OFFSET, LEDGER_STATUS_CODES } from './constants.js';
import { bytes, dec, hex, utf8 } from './convert.js';
import { Rpc } from './rpc.js';
import { ChangeBlock, ReceiveBlock, SendBlock } from './block.js';
export class Ledger {
    static #isInternal = false;
    #status = 'DISCONNECTED';
    get status() { return this.#status; }
    openTimeout = 3000;
    listenTimeout = 30000;
    transport = null;
    DynamicTransport = TransportHID;
    constructor() {
        if (!Ledger.#isInternal) {
            throw new Error('Ledger cannot be instantiated directly. Use Ledger.init()');
        }
        Ledger.#isInternal = false;
    }
    static async init() {
        Ledger.#isInternal = true;
        const self = new this();
        await self.checkBrowserSupport();
        await self.listen();
        return self;
    }
    /**
    * Check which transport protocols are supported by the browser and set the
    * transport type according to the following priorities: Bluetooth, USB, HID.
    */
    async checkBrowserSupport() {
        console.log('Checking browser Ledger support...');
        const supports = {
            ble: await TransportBLE.isSupported(),
            usb: await TransportUSB.isSupported(),
            hid: await TransportHID.isSupported()
        };
        console.log(`ble: ${supports.ble}; usb: ${supports.usb}; hid: ${supports.hid}`);
        if (supports.ble) {
            this.DynamicTransport = TransportBLE;
        }
        else if (supports.usb) {
            this.DynamicTransport = TransportUSB;
        }
        else if (supports.hid) {
            this.DynamicTransport = TransportHID;
        }
        else {
            throw new Error('Unsupported browser');
        }
    }
    async listen() {
        const { usb } = globalThis.navigator;
        if (usb) {
            usb.addEventListener('connect', console.log.bind(console));
            usb.addEventListener('disconnect', console.log.bind(console));
        }
    }
    async connect() {
        const { usb } = globalThis.navigator;
        if (usb) {
            usb.removeEventListener('disconnect', this.onDisconnectUsb.bind(this));
            usb.addEventListener('disconnect', this.onDisconnectUsb.bind(this));
        }
        const version = await this.version();
        if (version.status === 'OK') {
            if (version.name === 'Nano') {
                const account = await this.account();
                if (account.status === 'OK') {
                    this.#status = 'CONNECTED';
                }
                else if (account.status === 'SECURITY_STATUS_NOT_SATISFIED') {
                    this.#status = 'LOCKED';
                }
                else {
                    this.#status = 'DISCONNECTED';
                }
            }
            else if (version.name === 'BOLOS') {
                const open = await this.open();
                this.#status = (open.status === 'OK')
                    ? 'CONNECTED'
                    : 'DISCONNECTED';
            }
            else {
                this.#status = 'BUSY';
            }
        }
        else {
            this.#status = 'DISCONNECTED';
        }
        return this.status;
    }
    async onDisconnectUsb(e) {
        if (e.device?.manufacturerName === 'Ledger') {
            const { usb } = globalThis.navigator;
            usb.removeEventListener('disconnect', this.onDisconnectUsb);
            this.#status = 'DISCONNECTED';
        }
    }
    /**
    * Open Nano app by launching user flow.
    *
    * https://developers.ledger.com/docs/connectivity/ledgerJS/open-close-info-on-apps#open-application
    *
    * This command resets the internal USB connection of the device which can
    * cause subsequent commands to fail if called too quickly. A one-second delay
    * is implemented in this method to mitigate the issue.
    *
    * https://github.com/LedgerHQ/ledger-live/issues/4964#issuecomment-1878361157
    *
    * @returns Status of command
    */
    async open() {
        const name = new TextEncoder().encode('Nano');
        const transport = await this.DynamicTransport.create(this.openTimeout, this.listenTimeout);
        const response = await transport.send(0xe0, 0xd8, 0x00, 0x00, name)
            .then(res => bytes.toDec(res))
            .catch(err => err.statusCode);
        return new Promise(resolve => setTimeout(resolve, 1000, { status: LEDGER_STATUS_CODES[response] }));
    }
    /**
    * Close the currently running app.
    *
    * https://developers.ledger.com/docs/connectivity/ledgerJS/open-close-info-on-apps#quit-application
    *
    * This command resets the internal USB connection of the device which can
    * cause subsequent commands to fail if called too quickly. A one-second delay
    * is implemented in this method to mitigate the issue.
    *
    * https://github.com/LedgerHQ/ledger-live/issues/4964#issuecomment-1878361157
    *
    * @returns Status of command
    */
    async close() {
        const transport = await this.DynamicTransport.create(this.openTimeout, this.listenTimeout);
        const response = await transport.send(0xb0, 0xa7, 0x00, 0x00)
            .then(res => bytes.toDec(res))
            .catch(err => err.statusCode);
        return new Promise(resolve => setTimeout(resolve, 1000, { status: LEDGER_STATUS_CODES[response] }));
    }
    /**
    * Get the version of the current process. If a specific app is running, get
    * the app version. Otherwise, get the Ledger BOLOS version instead.
    *
    * https://developers.ledger.com/docs/connectivity/ledgerJS/open-close-info-on-apps#get-information
    *
    * @returns Status, process name, and version
    */
    async version() {
        const transport = await this.DynamicTransport.create(this.openTimeout, this.listenTimeout);
        const response = await transport.send(0xb0, 0x01, 0x00, 0x00)
            .catch(err => dec.toBytes(err.statusCode));
        await transport.close();
        if (response.length === 2) {
            const statusCode = bytes.toDec(response);
            const status = LEDGER_STATUS_CODES[statusCode] ?? 'UNKNOWN_ERROR';
            return { status, name: null, version: null };
        }
        const nameLength = response[1];
        const name = response.slice(2, 2 + nameLength).toString();
        const versionLength = response[2 + nameLength];
        const version = response.slice(2 + nameLength + 1, 2 + nameLength + 1 + versionLength).toString();
        const statusCode = bytes.toDec(response.slice(-2));
        const status = LEDGER_STATUS_CODES[statusCode];
        return { status, name, version };
    }
    /**
    * Get an account at a specific BIP-44 index.
    *
    * @returns Response object containing command status, public key, and address
    */
    async account(index = 0, show = false) {
        if (typeof index !== 'number' || index < 0 || index >= HARDENED_OFFSET) {
            throw new TypeError('Invalid account index');
        }
        const purpose = dec.toBytes(BIP44_PURPOSE + HARDENED_OFFSET, 4);
        const coin = dec.toBytes(BIP44_COIN_NANO + HARDENED_OFFSET, 4);
        const account = dec.toBytes(index + HARDENED_OFFSET, 4);
        const data = new Uint8Array([0x03, ...purpose, ...coin, ...account]);
        const transport = await this.DynamicTransport.create(this.openTimeout, this.listenTimeout);
        const response = await transport.send(0xa1, 0x02, show ? 0x01 : 0x00, 0x00, data)
            .catch(err => dec.toBytes(err.statusCode));
        await transport.close();
        if (response.length === 2) {
            const statusCode = bytes.toDec(response);
            const status = LEDGER_STATUS_CODES[statusCode] ?? 'UNKNOWN_ERROR';
            return { status, publicKey: null, address: null };
        }
        try {
            const publicKey = bytes.toHex(response.slice(0, 32));
            const addressLength = response[32];
            const address = response.slice(33, 33 + addressLength).toString();
            const statusCode = bytes.toDec(response.slice(33 + addressLength));
            const status = LEDGER_STATUS_CODES[statusCode];
            return { status, publicKey, address };
        }
        catch (err) {
            return { status: 'ERROR_PARSING_ACCOUNT', publicKey: null, address: null };
        }
    }
    /**
    * Cache frontier block in device memory.
    *
    * @param {number} index - Account number
    * @param {any} block - Block data to cache
    * @returns Status of command
    */
    async cacheBlock(index = 0, block) {
        if (typeof index !== 'number' || index < 0 || index >= HARDENED_OFFSET) {
            throw new TypeError('Invalid account index');
        }
        if (!(block instanceof SendBlock) && !(block instanceof ReceiveBlock) && !(block instanceof ChangeBlock)) {
            throw new TypeError('Invalid block format');
        }
        if (!block.signature) {
            throw new ReferenceError('Cannot cache unsigned block');
        }
        const purpose = dec.toBytes(BIP44_PURPOSE + HARDENED_OFFSET, 4);
        const coin = dec.toBytes(BIP44_COIN_NANO + HARDENED_OFFSET, 4);
        const account = dec.toBytes(index + HARDENED_OFFSET, 4);
        const previous = hex.toBytes(block.previous);
        const link = hex.toBytes(block.link);
        const representative = hex.toBytes(block.representative.publicKey);
        const balance = hex.toBytes(BigInt(block.balance).toString(16), 16);
        const signature = hex.toBytes(block.signature);
        const data = new Uint8Array([0x03, ...purpose, ...coin, ...account, ...previous, ...link, ...representative, ...balance, ...signature]);
        const transport = await this.DynamicTransport.create(this.openTimeout, this.listenTimeout);
        const response = await transport.send(0xa1, 0x03, 0x00, 0x00, data)
            .then(res => bytes.toDec(res))
            .catch(err => err.statusCode);
        await transport.close();
        return { status: LEDGER_STATUS_CODES[response] };
    }
    async sign(index = 0, input) {
        if (typeof index !== 'number' || index < 0 || index >= HARDENED_OFFSET) {
            throw new TypeError('Invalid account index');
        }
        const purpose = dec.toBytes(BIP44_PURPOSE + HARDENED_OFFSET, 4);
        const coin = dec.toBytes(BIP44_COIN_NANO + HARDENED_OFFSET, 4);
        const account = dec.toBytes(index + HARDENED_OFFSET, 4);
        const transport = await this.DynamicTransport.create(this.openTimeout, this.listenTimeout);
        if (typeof input === 'string') {
            // input is a nonce
            const nonce = utf8.toBytes(input);
            if (nonce.length !== 16) {
                throw new RangeError('Nonce must be 16-byte string');
            }
            const data = new Uint8Array([0x03, ...purpose, ...coin, ...account, ...nonce]);
            const response = await transport.send(0xa1, 0x05, 0x00, 0x00, data)
                .catch(err => dec.toBytes(err.statusCode));
            await transport.close();
            if (response.length === 2) {
                const statusCode = bytes.toDec(response);
                const status = LEDGER_STATUS_CODES[statusCode] ?? 'UNKNOWN_ERROR';
                return { status, signature: null };
            }
            const signature = bytes.toHex(response.slice(0, 64));
            const statusCode = bytes.toDec(response.slice(-2));
            const status = LEDGER_STATUS_CODES[statusCode];
            return { status, signature };
        }
        else {
            // input is a block
            const previous = hex.toBytes(input.previous);
            const link = hex.toBytes(input.link);
            const representative = hex.toBytes(input.representative.publicKey);
            const balance = hex.toBytes(BigInt(input.balance).toString(16), 16);
            const data = new Uint8Array([0x03, ...purpose, ...coin, ...account, ...previous, ...link, ...representative, ...balance]);
            const response = await transport.send(0xa1, 0x04, 0x00, 0x00, data)
                .catch(err => dec.toBytes(err.statusCode));
            await transport.close();
            if (response.length === 2) {
                const statusCode = bytes.toDec(response);
                const status = LEDGER_STATUS_CODES[statusCode] ?? 'UNKNOWN_ERROR';
                return { status, signature: null, hash: null };
            }
            const hash = bytes.toHex(response.slice(0, 32));
            const signature = bytes.toHex(response.slice(32, 96));
            const statusCode = bytes.toDec(response.slice(-2));
            const status = LEDGER_STATUS_CODES[statusCode];
            return { status, signature, hash };
        }
    }
    async updateCache(index, input, node) {
        if (typeof input === 'string' && node?.constructor === Rpc) {
            const data = {
                'json_block': 'true',
                'hash': input
            };
            const res = await node.call('block_info', data);
            if (!res || res.ok === false) {
                throw new Error(`Unable to fetch block info`, res);
            }
            input = res.contents;
        }
        return this.cacheBlock(index, input);
    }
}
